
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Checkbox,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TablePagination,
  TextField,
  Tooltip

} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";
import { connect } from "react-redux";
import { setLoading, setSnackInfo } from "../../redux/actions/appAction";
import HttpService from "../../services/httpService";
import { IoMdDownload } from "react-icons/io";
import * as XLSX from 'xlsx';

const useStyles = makeStyles(() => ({
  tableContainer: {
    margin: "10px auto",
    width: "auto",
    borderRadius: "5px",
    overflow: "hidden",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
  },
  tableHead: {
    backgroundColor: "#1C375D",
    height: "40px",
  },
  tableHeadCell: {
    fontWeight: "bold",
    fontSize: "14px",
    padding: "8px",
    textAlign: "center",
    color: "#ffffff",
  },
  tableRow: {
    "&:nth-of-type(odd)": {
      backgroundColor: "#f9f9f9",
    },
    "&:hover": {
      backgroundColor: "#e6f7ff",
      cursor: "pointer",
    },
  },
  tableCell: {
    padding: "10px",
    textAlign: "center",
    fontSize: "14px",
  },
  button: {
    marginTop: "10px",
    backgroundColor: "#1976d2",
    color: "#FFF2EB",
    "&:hover": {
      backgroundColor: "#135ba1",
    },
  },
}));

function LogDetailed(props) {
  const classes = useStyles();
  const [selectedLogType, setSelectedLogType] = useState("user");
  const [userLogs, setUserLogs] = useState([]);
  const [teamLogs, setTeamLogs] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(250);
  const [totalCount, setTotalCount] = useState(0);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Submit date filter
  const handleDateFilterSubmit = () => {
    // Reset to first page when applying filters
    setPage(0);
    fetchLogData(1, rowsPerPage, dateRange.startDate, dateRange.endDate);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setDateRange({
      startDate: "",
      endDate: ""
    });
    setPage(0);
    fetchLogData(1, rowsPerPage);
  };

  // Pagination function
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchLogData(page + 1, rowsPerPage, dateRange.startDate, dateRange.endDate);
  }, [page, rowsPerPage]);

  const fetchLogData = async (pageNumber = 1, limit = 250, startDate = "", endDate = "") => {
    props.setLoading(true);
    try {
      const response = await HttpService.logDetailed(pageNumber, limit, startDate, endDate);

      if (response?.data) {
        setUserLogs(response?.data?.userLogs || []);
        setTeamLogs(response?.data?.teamLogs || []);
        setTotalCount(response?.data?.pagination?.total || 0);
        props.setSnackInfo("Data loaded successfully");
      }
    } catch (error) {
      console.error("Failed to fetch log data", error);
      props.setSnackInfo("Failed to fetch log data", "error");
    } finally {
      props.setLoading(false);
    }
  };

  // Handle CSV export
  const handleExportCSV = () => {
    try {
      // Get current data based on selected log type
      const currentData = selectedLogType === "user" ? userLogs : teamLogs;

      if (!currentData || currentData.length === 0) {
        props.setSnackInfo("No data available to export");
        return;
      }

      // Define worksheet headers
      const headers = ["Name", "Change Type", "Previous Value", "New Value", "Effective Date", "Changed By"];

      // Format data for workbook
      const excelData = currentData.map(log => {
        // Extract name based on log type
        const name = selectedLogType === "user"
          ? (log?.userId?.name || "N/A")
          : (log?.team?.name || "N/A");

        // Format previous and new values - keep all values in one cell
        const formatValue = (items) => {
          if (!items || items.length === 0) return "—";

          const values = Array.isArray(items) ? items : [items];
          const names = values.map(v => v?.name).filter(Boolean);

          return names.join(", ") || "—";
        };

        // Format date
        const formattedDate = log.effectiveDate
          ? moment(log.effectiveDate).format("DD-MM-YYYY ~ hh:mm A")
          : "N/A";

        // Return row data
        return {
          "Name": name,
          "Change Type": log?.changeType || "N/A",
          "Previous Value": formatValue(log.previousValue),
          "New Value": formatValue(log.newValue),
          "Effective Date": formattedDate,
          "Changed By": log?.changedBy?.name || "N/A"
        };
      });

      // Create worksheet from data
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Create workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, selectedLogType === "user" ? "User Logs" : "Team Logs");

      // Generate Excel file and trigger download
      XLSX.writeFile(workbook, `${selectedLogType}_logs_${moment().format("YYYY-MM-DD")}.xlsx`);

      props.setSnackInfo("Excel file downloaded successfully");

    } catch (error) {
      console.error("Excel export failed", error);
      props.setSnackInfo("Failed to download Excel file", "error");
    }
  };

  const getDisplayName = (items) => {
    if (!items || items.length === 0) return "—";

    const values = Array.isArray(items) ? items : [items];
    const names = values.map((v) => v?.name).filter(Boolean);

    const displayNames = names.slice(0, 2).map((name, index) => {
      const truncated = name.length > 19 ? `${name.slice(0, 19)}..` : name;
      return (
        <span key={index}>
          {truncated}
          {index < names.length - 1 && names.length > 1 ? ", " : ""}
        </span>
      );
    });

    const extraText = names.length > 2 ? ".." : "";

    return (
      <Tooltip title={names.join(", ")} arrow placement="top">
        <span>
          {displayNames}
          {extraText}
        </span>
      </Tooltip>
    );
  };

  const renderTable = (logs, isTeam = false) => (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table className={classes.table}>
        <TableHead className={classes.tableHead}>
          <TableRow>
          <TableCell className={classes.tableHeadCell}>Sl No</TableCell>
            <TableCell className={classes.tableHeadCell}>
              {isTeam ? "Team" : "User"}
            </TableCell>
            <TableCell className={classes.tableHeadCell}>Change Type</TableCell>
            <TableCell className={classes.tableHeadCell}>Previous Value</TableCell>
            <TableCell className={classes.tableHeadCell}>New Value</TableCell>
            <TableCell className={classes.tableHeadCell}>Effective Date</TableCell>
            <TableCell className={classes.tableHeadCell}>Changed By</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {logs && logs.length > 0 ? (
            logs.map((log, index) => (
              <TableRow key={index} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>
                  {page * rowsPerPage + index + 1}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {isTeam ? log?.team?.name || "N/A" : log?.userId?.name || "N/A"}
                </TableCell>
                <TableCell className={classes.tableCell}>{log?.changeType}</TableCell>
                <TableCell className={classes.tableCell}>{getDisplayName(log.previousValue)}</TableCell>
                <TableCell className={classes.tableCell}>{getDisplayName(log.newValue)}</TableCell>
                <TableCell className={classes.tableCell}>
                  {log.effectiveDate ? moment(log.effectiveDate).format("DD-MM-YYYY ~ hh:mm A") : "N/A"}
                </TableCell>
                <TableCell className={classes.tableCell}>{log?.changedBy?.name || "N/A"}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center">
                No logs found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <div>
      <Paper className={classes.paper} style={{ padding: "16px", marginBottom: "16px" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={3} width="80%">
          <FormControl
            variant="outlined"
            style={{
              minWidth: "150px",
              marginRight: "20px",
              marginTop: "10px"
            }}
            size="small"
          >
            <InputLabel id="log-type-label">Log Type</InputLabel>
            <Select
              labelId="log-type-label"
              value={selectedLogType}
              onChange={(e) => setSelectedLogType(e.target.value)}
              label="Log Type"
              sx={{
                height: '50px',
                '& .MuiSelect-select': {
                  paddingTop: '7px',
                  paddingBottom: '7px',
                },
              }}
            >
              <MenuItem value="user">User Logs</MenuItem>
              <MenuItem value="team">Team Logs</MenuItem>
            </Select>
          </FormControl>

          {/* From Date */}
          <TextField
            id="date"
            name="startDate"
            label="From Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            className={classes.textField}
            fullWidth
            value={dateRange.startDate}
            onChange={handleDateChange}
            style={{
              marginRight: "20px",
            }}
          />

          {/* End Date */}
          <TextField
            id="endDate"
            name="endDate"
            label="End Date"
            type="date"
            value={dateRange.endDate}
            onChange={handleDateChange}
            InputLabelProps={{ shrink: true }}
            className={classes.textField}
            fullWidth
          />

          {/* Submit Button */}
          <Button
            variant="contained"
            color="primary"
            style={{
              height: "40px",
              width: "60%",
              marginLeft: "40px",
              backgroundColor: !dateRange.startDate || !dateRange.endDate
                ? "rgba(244, 126, 70, 0.5)"
                : "rgb(244, 126, 70)",
              color: "white",
              opacity: !dateRange.startDate || !dateRange.endDate ? 0.7 : 1
            }}
            disabled={!dateRange.startDate || !dateRange.endDate}
            onClick={handleDateFilterSubmit}
          >
            Submit
          </Button>

          {/* Clear Filter  */}
          <Button
            variant="contained"
            color="secondary"
            style={{ height: "40px", width: "60%", marginLeft: "40px" }}
            onClick={handleClearFilters}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="secondary"
            style={{
              height: "40px",
              width: "60%",
              marginLeft: "40px",
              backgroundColor: "#4CAF50",
              color: "white"
            }}
            onClick={handleExportCSV}
          >
            <span style={{ marginRight: "10px", display: "flex", alignItems: "center" }}>
              <IoMdDownload size={20} />
            </span>
            CSV
          </Button>
        </Box>
      </Paper>
      {selectedLogType === "user"
        ? renderTable(userLogs, false)
        : renderTable(teamLogs, true)}

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[250, 400, 500, 600, 800, 1000]}
      />
    </div>
  );
}

export default connect(null, { setLoading, setSnackInfo })(LogDetailed);
